import { useCallback, useEffect, useRef, useState } from 'react';

import type { ChatMessageResponse, ChatSessionResponse, SessionStateSnapshot } from '../lib/api';
import { getChatSession } from '../lib/api';
import { maybeJsonRecord } from '../lib/runtime-validation';

export type ChatConnectionState = 'connecting' | 'connected' | 'reconnecting' | 'disconnected';

const BASE_RECONNECT_DELAY = 1000;
const MAX_RECONNECT_DELAY = 30000;
const MAX_RETRIES = 10;
const PING_INTERVAL_MS = parseInt(import.meta.env.VITE_WS_PING_INTERVAL_MS || '30000');
const PONG_TIMEOUT_MS = parseInt(import.meta.env.VITE_WS_PONG_TIMEOUT_MS || '10000');

interface UseChatWebSocketOptions {
  projectId: string;
  sessionId: string;
  /** Only connect when the session is active. */
  enabled: boolean;
  /** Called when a new message arrives via WebSocket (single or from batch). */
  onMessage: (msg: ChatMessageResponse) => void;
  /** Called when the session is stopped server-side. */
  onSessionStopped: () => void;
  /** Called when we catch up with missed messages after reconnect. */
  onCatchUp: (messages: ChatMessageResponse[], session: ChatSessionResponse, state?: SessionStateSnapshot | null) => void;
  /** Called when the agent completes on the session. */
  onAgentCompleted?: (agentCompletedAt: number) => void;
  /** Called when a session.activity event arrives (prompting/idle). */
  onAgentActivity?: (activity: 'prompting' | 'idle', promptStartedAt?: number | null) => void;
}

export interface UseChatWebSocketReturn {
  connectionState: ChatConnectionState;
  wsRef: React.RefObject<WebSocket | null>;
  retry: () => void;
}

/**
 * WebSocket hook for chat sessions with exponential backoff reconnection
 * and message catch-up on reconnect (TDF-8).
 *
 * Follows the same pattern as useAdminLogStream for consistency.
 */
export function useChatWebSocket({
  projectId,
  sessionId,
  enabled,
  onMessage,
  onSessionStopped,
  onCatchUp,
  onAgentCompleted,
  onAgentActivity,
}: UseChatWebSocketOptions): UseChatWebSocketReturn {
  const [connectionState, setConnectionState] = useState<ChatConnectionState>('disconnected');

  const wsRef = useRef<WebSocket | null>(null);
  const retriesRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const pongTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const mountedRef = useRef(true);
  const connectRef = useRef<() => void>(() => {});
  const hadConnectionRef = useRef(false);

  // Keep callbacks stable via refs
  const onMessageRef = useRef(onMessage);
  const onSessionStoppedRef = useRef(onSessionStopped);
  const onCatchUpRef = useRef(onCatchUp);
  const onAgentCompletedRef = useRef(onAgentCompleted);
  const onAgentActivityRef = useRef(onAgentActivity);
  onMessageRef.current = onMessage;
  onSessionStoppedRef.current = onSessionStopped;
  onCatchUpRef.current = onCatchUp;
  onAgentCompletedRef.current = onAgentCompleted;
  onAgentActivityRef.current = onAgentActivity;

  const getReconnectDelay = useCallback((attempt: number) => {
    return Math.min(BASE_RECONNECT_DELAY * Math.pow(2, attempt), MAX_RECONNECT_DELAY);
  }, []);

  const scheduleReconnect = useCallback(() => {
    if (!mountedRef.current) return;
    if (retriesRef.current >= MAX_RETRIES) {
      setConnectionState('disconnected');
      return;
    }

    setConnectionState('reconnecting');
    const delay = getReconnectDelay(retriesRef.current);
    retriesRef.current++;

    reconnectTimerRef.current = setTimeout(() => {
      if (mountedRef.current) {
        connectRef.current();
      }
    }, delay);
  }, [getReconnectDelay]);

  const connect = useCallback(() => {
    if (!mountedRef.current) return;

    setConnectionState(retriesRef.current === 0 ? 'connecting' : 'reconnecting');

    // Clean up existing socket
    if (wsRef.current) {
      wsRef.current.close(1000);
      wsRef.current = null;
    }

    const API_URL = import.meta.env.VITE_API_URL || '';
    const wsUrl = API_URL.replace(/^http/, 'ws') + `/api/projects/${projectId}/sessions/ws?sessionId=${encodeURIComponent(sessionId)}`;

    try {
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        if (!mountedRef.current) {
          ws.close(1000);
          return;
        }
        const wasReconnect = hadConnectionRef.current;
        retriesRef.current = 0;
        setConnectionState('connected');
        hadConnectionRef.current = true;

        // Only catch up on reconnect — the initial REST load (loadSession)
        // already fetches messages, so a duplicate catch-up on first connect
        // races with it and can overwrite messages via the 'replace' merge
        // strategy, causing them to briefly appear then disappear.
        if (wasReconnect) {
          void catchUpMessages();
        }
      };

      ws.onmessage = (event) => {
        if (!mountedRef.current || wsRef.current !== ws) return;

        try {
          const data = JSON.parse(event.data);
          const payload = data.payload ?? data;

          // Handle pong response — clear the pong timeout to indicate the
          // connection is alive. Without this, the timeout would fire and
          // force-close the socket even though the server is responsive.
          if (data.type === 'pong') {
            clearTimeout(pongTimeoutRef.current);
            return;
          }

          if (data.type === 'message.new') {
            const p = payload;
            if (p.sessionId !== sessionId) return;
            // Only deliver if content is present (broadcast now includes it)
            if (!p.content) return;
            const newMsg: ChatMessageResponse = {
              id: p.messageId || p.id || crypto.randomUUID(),
              sessionId: p.sessionId,
              role: p.role,
              content: p.content,
              toolMetadata: p.toolMetadata || null,
              createdAt: p.createdAt || Date.now(),
              sequence: p.sequence ?? null,
            };
            onMessageRef.current(newMsg);
          } else if (data.type === 'messages.batch') {
            const p = payload;
            if (p.sessionId !== sessionId) return;
            const msgs: ChatMessageResponse[] = (p.messages ?? [])
              .filter((m: Record<string, unknown>) => m.content)
              .map((m: Record<string, unknown>) => ({
                id: (m.id as string) || crypto.randomUUID(),
                sessionId: sessionId,
                role: (m.role as string) || 'assistant',
                content: m.content as string,
                toolMetadata: maybeJsonRecord(m.toolMetadata),
                createdAt: (m.createdAt as number) || Date.now(),
                sequence: (m.sequence as number) ?? null,
              }));
            for (const msg of msgs) {
              onMessageRef.current(msg);
            }
          } else if (data.type === 'session.stopped' || data.type === 'session.failed') {
            const p = payload;
            if (p.sessionId !== sessionId) return;
            onSessionStoppedRef.current();
          } else if (data.type === 'session.agent_completed') {
            const p = payload;
            if (p.sessionId !== sessionId) return;
            onAgentCompletedRef.current?.(p.agentCompletedAt ?? Date.now());
          } else if (data.type === 'session.activity') {
            const p = payload;
            if (p.sessionId !== sessionId) return;
            if (p.activity === 'prompting' || p.activity === 'idle') {
              onAgentActivityRef.current?.(p.activity, p.promptStartedAt ?? null);
            }
          }
        } catch {
          // Ignore malformed messages
        }
      };

      ws.onclose = (event) => {
        if (!mountedRef.current) return;
        // Guard: only handle close if this is still the active socket.
        // A newer connect() call may have already replaced wsRef.current.
        if (wsRef.current !== ws) return;
        wsRef.current = null;
        if (event.code !== 1000) {
          scheduleReconnect();
        } else {
          setConnectionState('disconnected');
        }
      };

      ws.onerror = () => {
        // Error is followed by close event
      };

      wsRef.current = ws;
    } catch {
      scheduleReconnect();
    }
  }, [projectId, sessionId, scheduleReconnect]);

  connectRef.current = connect;

  const catchUpMessages = useCallback(async () => {
    try {
      const data = await getChatSession(projectId, sessionId);
      onCatchUpRef.current(data.messages, data.session, data.state);
    } catch {
      // Best-effort catch-up — poll fallback will handle it
    }
  }, [projectId, sessionId]);

  // Ping keep-alive with pong timeout detection.
  // Sends a JSON ping every PING_INTERVAL_MS. After each ping, starts a
  // PONG_TIMEOUT_MS timer — if no pong arrives before it fires, the connection
  // is considered dead and force-closed to trigger the reconnect path.
  useEffect(() => {
    if (!enabled) return;
    const interval = setInterval(() => {
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'ping' }));

        // Start pong deadline — if the server doesn't respond within
        // PONG_TIMEOUT_MS, the connection is silently dead (e.g. Cloudflare
        // proxy dropped it). Force-close with a non-1000 code to trigger
        // the reconnect path in onclose.
        clearTimeout(pongTimeoutRef.current);
        pongTimeoutRef.current = setTimeout(() => {
          const current = wsRef.current;
          if (current && current.readyState === WebSocket.OPEN) {
            current.close(4000, 'pong timeout');
          }
        }, PONG_TIMEOUT_MS);
      }
    }, PING_INTERVAL_MS);
    return () => {
      clearInterval(interval);
      clearTimeout(pongTimeoutRef.current);
    };
  }, [enabled]);

  // Connection lifecycle
  useEffect(() => {
    if (!enabled) {
      // Disconnect when disabled
      if (wsRef.current) {
        wsRef.current.close(1000);
        wsRef.current = null;
      }
      clearTimeout(reconnectTimerRef.current);
      clearTimeout(pongTimeoutRef.current);
      setConnectionState('disconnected');
      hadConnectionRef.current = false;
      retriesRef.current = 0;
      return;
    }

    mountedRef.current = true;
    connectRef.current();

    return () => {
      mountedRef.current = false;
      clearTimeout(reconnectTimerRef.current);
      clearTimeout(pongTimeoutRef.current);
      if (wsRef.current) {
        wsRef.current.close(1000);
        wsRef.current = null;
      }
    };
  }, [enabled, projectId, sessionId]);

  const retry = useCallback(() => {
    retriesRef.current = 0;
    // Keep hadConnectionRef.current = true so catch-up fires on reconnect
    clearTimeout(reconnectTimerRef.current);
    connectRef.current();
  }, []);

  return {
    connectionState,
    wsRef,
    retry,
  };
}
